import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ReactNode } from 'react';

export interface DropdownMenuItem {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  dividerAfter?: boolean;
}

interface DropdownMenuProps {
  label: string;
  items: DropdownMenuItem[];
  darkMode?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export function DropdownMenu({
  label,
  items,
  darkMode = false,
  size = 'md',
  className = '',
}: DropdownMenuProps) {
  const sizeClasses = {
    xs: 'px-2 py-1.5 text-sm',
    sm: 'px-2 py-1.5 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-3 py-2 text-sm',
    xl: 'px-3.5 py-2.5 text-sm',
  };

  return (
    <Menu as="div" className={`relative ${className}`}>
      <MenuButton
        className={`inline-flex items-center gap-2 rounded-md font-semibold shadow-xs ring-1 transition ring-inset focus:outline-none data-hover:opacity-90 ${sizeClasses[size]} ${
          darkMode
            ? 'bg-gray-800 text-gray-100 ring-white/10 data-hover:bg-gray-700'
            : 'bg-white text-gray-900 ring-gray-300 data-hover:bg-gray-50'
        }`}
      >
        {label}
        <ChevronDownIcon className="size-4" />
      </MenuButton>

      <MenuItems
        transition
        anchor="bottom end"
        className={`z-50 w-48 origin-top-right rounded-lg p-1 text-sm/6 shadow-lg ring-1 transition duration-100 ease-out [--anchor-gap:0.25rem] focus:outline-none data-closed:scale-95 data-closed:opacity-0 ${
          darkMode
            ? 'bg-gray-800 text-gray-300 ring-white/10'
            : 'bg-white text-gray-900 ring-gray-900/5'
        }`}
      >
        {items.map((item, index) => (
          <div key={index}>
            <MenuItem>
              <button
                onClick={item.onClick}
                className={`group flex w-full items-center gap-x-3 rounded-md px-3 py-2 transition ${
                  darkMode ? 'data-focus:bg-gray-700' : 'data-focus:bg-gray-50'
                }`}
              >
                {item.icon && <span className="size-5">{item.icon}</span>}
                {item.label}
              </button>
            </MenuItem>
            {item.dividerAfter && (
              <div className={`my-1 h-px ${darkMode ? 'bg-white/10' : 'bg-gray-100'}`} />
            )}
          </div>
        ))}
      </MenuItems>
    </Menu>
  );
}
